import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar';

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const base = process.env.REACT_APP_API_URL;

  // form fields (unchanged)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    showPrice: true, // Default to show price
    whatsappUrl: '',
    images: []       // store URL strings here (existing +, later, merged)
  });

  // NEW: track existing gallery separately from newly picked files
  const [existingImages, setExistingImages] = useState([]); // URLs already in DB
  const [newFiles, setNewFiles] = useState([]);             // File objects to upload
  const [newPreviews, setNewPreviews] = useState([]);       // object URLs for preview only

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const isAdminLoggedIn = localStorage.getItem('isAdminLoggedIn');
    if (!isAdminLoggedIn) {
      navigate('/admin');
    } else {
      fetchProduct();
    }
    // eslint-disable-next-line
  }, [id, navigate]);

  // Fetch product by ID
  const fetchProduct = async () => {
    setFetchLoading(true);
    setError('');
    try {
      const response = await fetch(`${base}/api/products/${id}`);
      const data = await response.json();

      if (response.ok && data.success) {
        const p = data.data;
        const imgs = Array.isArray(p.images) ? p.images : [];

        setFormData({
          name: p.name,
          description: p.description,
          price: p.price,
          showPrice: p.showPrice !== undefined ? p.showPrice : true,
          whatsappUrl: p.whatsappUrl,
          images: imgs
        });

        setExistingImages(imgs); // keep a copy so we can manage removes cleanly
        setNewFiles([]);
        // clear any previous object URLs
        newPreviews.forEach((u) => URL.revokeObjectURL(u));
        setNewPreviews([]);
      } else {
        throw new Error(data.message || 'Failed to fetch product');
      }
    } catch (err) {
      setError('An error occurred while fetching the product');
      console.error(err);
    } finally {
      setFetchLoading(false);
    }
  };

  // Handle text input changes (unchanged)
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'price' ? Number(value) : value)
    }));
  };

  // Handle image upload (no base64; respect the 5-image total cap)
  const handleImageChange = (e) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);

    const totalAlready = existingImages.length + newPreviews.length;
    const remainingSlots = 5 - totalAlready;

    if (remainingSlots <= 0) {
      setError('You already have 5 images');
      return;
    }
    if (files.length > remainingSlots) {
      setError(`You can only upload ${remainingSlots} more image${remainingSlots !== 1 ? 's' : ''}`);
      return;
    }

    setError('');
    setNewFiles((prev) => [...prev, ...files]);
    const previews = files.map((f) => URL.createObjectURL(f));
    setNewPreviews((prev) => [...prev, ...previews]);
  };

  // Remove image from the single preview grid.
  // Grid is [ ...existingImages, ...newPreviews ] in that order.
  const removeImage = (index) => {
    const totalExisting = existingImages.length;

    if (index < totalExisting) {
      // Removing an existing URL → drop it from existingImages and formData.images
      const toRemoveUrl = existingImages[index];
      const nextExisting = existingImages.filter((_, i) => i !== index);
      setExistingImages(nextExisting);
      setFormData((prev) => ({
        ...prev,
        images: prev.images.filter((url) => url !== toRemoveUrl)
      }));
    } else {
      // Removing a newly-added preview/file
      const newIndex = index - totalExisting;
      const toRevoke = newPreviews[newIndex];
      if (toRevoke) URL.revokeObjectURL(toRevoke);

      const nextPreviews = [...newPreviews];
      nextPreviews.splice(newIndex, 1);
      setNewPreviews(nextPreviews);

      const nextFiles = [...newFiles];
      nextFiles.splice(newIndex, 1);
      setNewFiles(nextFiles);
    }
  };

  // Helper: upload a single file -> returns public URL
  const uploadOne = async (productId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${base}/api/upload?productId=${productId}`, { method: 'POST', body: fd });
    const data = await res.json();
    if (!res.ok || !data.fileUrl) throw new Error(data.error || 'Upload failed');
    return data.fileUrl;
  };

  // Submit form: update text → upload new files → set main (first new) → save merged gallery
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!formData.name || !formData.description || !formData.whatsappUrl || formData.price === '') {
      setError('Please fill out all required fields');
      setLoading(false);
      return;
    }

    try {
      // 1) Update text fields only
      const up = await fetch(`${base}/api/products/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          price: formData.price,
          showPrice: formData.showPrice,
          whatsappUrl: formData.whatsappUrl
          // IMPORTANT: don't send images yet; we merge after uploads
        })
      });
      const upd = await up.json();
      if (!up.ok || !upd.success) throw new Error(upd.message || 'Failed to update product');

      // 2) Upload any new files in order
      let uploadedUrls = [];
      for (let i = 0; i < newFiles.length; i++) {
        const url = await uploadOne(id, newFiles[i]);
        uploadedUrls.push(url);
      }

      // 3) If at least one new image, set it as main image
      if (uploadedUrls[0]) {
        const saveMain = await fetch(`${base}/api/products/${id}/image`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageUrl: uploadedUrls[0] })
        });
        if (!saveMain.ok) throw new Error('Failed to save main image');
      }

      // 4) Merge gallery and persist
      const merged = [...existingImages, ...uploadedUrls]; // existing first, then newly uploaded
      const saveGallery = await fetch(`${base}/api/products/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ images: merged })
      });
      const sg = await saveGallery.json();
      if (!saveGallery.ok || !sg.success) {
        throw new Error(sg.message || 'Failed to save gallery images');
      }

      // Cleanup preview URLs and state
      newPreviews.forEach((u) => URL.revokeObjectURL(u));
      setNewFiles([]);
      setNewPreviews([]);

      setExistingImages(merged);
      setFormData((prev) => ({ ...prev, images: merged }));

      setSuccess('Product updated successfully!');
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
      console.error('Update product error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Combined preview array for UI (keeps your single grid design)
  const combinedPreviews = [...existingImages, ...newPreviews];

  return (
    <div className="flex h-screen bg-gray-100">
      <AdminSidebar />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-sm z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <h1 className="text-2xl font-bold text-gray-900 font-playfair">Edit Product</h1>
              <button
                onClick={() => navigate('/admin/products')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 font-satoshi"
              >
                Back to Products
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-gray-50 p-4">
          {fetchLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto bg-white rounded-lg shadow p-6">
              {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                  <p className="text-sm text-red-700 font-satoshi">{error}</p>
                </div>
              )}
              
              {success && (
                <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
                  <p className="text-sm text-green-700 font-satoshi">{success}</p>
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                  {/* Product Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 font-satoshi">
                      Product Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-satoshi"
                      required
                    />
                  </div>
                  
                  {/* Description */}
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 font-satoshi">
                      Description *
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      rows={4}
                      value={formData.description}
                      onChange={handleChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-satoshi"
                      required
                    />
                  </div>
                  
                  {/* Price */}
                  <div>
                    <label htmlFor="price" className="block text-sm font-medium text-gray-700 font-satoshi">
                      Price (₹) *
                    </label>
                    <input
                      type="number"
                      id="price"
                      name="price"
                      min="0"
                      step="0.01"
                      value={formData.price}
                      onChange={handleChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-satoshi"
                      required
                    />
                  </div>

                  {/* Price Display Option */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 font-satoshi mb-3">
                      Price Display Option *
                    </label>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <input
                          id="showPrice"
                          name="showPrice"
                          type="radio"
                          checked={formData.showPrice === true}
                          onChange={() => setFormData({ ...formData, showPrice: true })}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                        />
                        <label htmlFor="showPrice" className="ml-3 block text-sm text-gray-900 font-satoshi">
                          Show Price (Display the actual price)
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          id="requestPrice"
                          name="showPrice"
                          type="radio"
                          checked={formData.showPrice === false}
                          onChange={() => setFormData({ ...formData, showPrice: false })}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                        />
                        <label htmlFor="requestPrice" className="ml-3 block text-sm text-gray-900 font-satoshi">
                          Request for Price (Show "Request for price" with WhatsApp link)
                        </label>
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-gray-500 font-satoshi">
                      Choose whether to display the actual price or show "Request for price" option
                    </p>
                  </div>
                  
                  {/* WhatsApp URL */}
                  <div>
                    <label htmlFor="whatsappUrl" className="block text-sm font-medium text-gray-700 font-satoshi">
                      WhatsApp URL *
                    </label>
                    <input
                      type="url"
                      id="whatsappUrl"
                      name="whatsappUrl"
                      value={formData.whatsappUrl}
                      onChange={handleChange}
                      placeholder="https://wa.me/91XXXXXXXXXX"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm font-satoshi"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500 font-satoshi">
                      Format: https://wa.me/91XXXXXXXXXX (include country code)
                    </p>
                  </div>
                  
                  {/* Images (picker) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 font-satoshi">
                      Product Images (Maximum 5)
                    </label>
                    <div className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                      <div className="space-y-1 text-center">
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                          aria-hidden="true"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div className="flex text-sm text-gray-600">
                          <label
                            htmlFor="images"
                            className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none"
                          >
                            <span className="font-satoshi">Upload images</span>
                            <input
                              id="images"
                              name="images"
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handleImageChange}
                              className="sr-only"
                            />
                          </label>
                          <p className="pl-1 font-satoshi">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500 font-satoshi">
                          PNG, JPG, GIF up to 10MB each
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Image Previews (single grid combining existing + new) */}
                  {combinedPreviews.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
                      {combinedPreviews.map((src, index) => (
                        <div key={index} className="relative">
                          <img
                            src={src}
                            alt={`Preview ${index + 1}`}
                            className="h-32 w-full object-cover rounded-md"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 m-1"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Submit Button */}
                  <div>
                    <button
                      type="submit"
                      disabled={loading}
                      className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 font-satoshi ${
                        loading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {loading ? 'Updating Product...' : 'Update Product'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default EditProduct;
